// ==================== EXPORT CONTACTS MODAL ====================

import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Download,
  FileSpreadsheet,
  FileJson,
  Loader2,
  CheckCircle,
  Users,
  Filter,
} from 'lucide-react';
import { Modal, Button, Checkbox, Badge, Card } from '../../../components/ui';
import { contactsService, type QueryContactsParams } from '../../../services/contacts.service';
import type { ContactGroup } from '../../../types';
import { getCountryByCode } from '../../../constants/countries.constants';

// ==================== TYPES ====================

type ExportFormat = 'csv' | 'json';

interface ExportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  groups: ContactGroup[];
  selectedTags: string[];
  selectedGroupIds: string[];
  optedInFilter: boolean | undefined;
  totalContacts: number;
}

interface ExportField {
  key: string;
  label: string;
  checked: boolean;
}

// ==================== CONSTANTS ====================

const DEFAULT_FIELDS: ExportField[] = [
  { key: 'phoneNumber', label: 'Phone Number', checked: true },
  { key: 'name', label: 'Name', checked: true },
  { key: 'email', label: 'Email', checked: true },
  { key: 'company', label: 'Company', checked: true },
  { key: 'country', label: 'Country', checked: true },
  { key: 'tags', label: 'Tags', checked: true },
  { key: 'notes', label: 'Notes', checked: false },
  { key: 'optedIn', label: 'Opted In Status', checked: true },
  { key: 'createdAt', label: 'Created Date', checked: true },
  { key: 'updatedAt', label: 'Updated Date', checked: false },
  { key: 'customFields', label: 'Custom Fields', checked: false },
];

// ==================== MAIN COMPONENT ====================

const ExportContactsModal = ({
  isOpen,
  onClose,
  groups,
  selectedTags,
  selectedGroupIds,
  optedInFilter,
  totalContacts,
}: ExportContactsModalProps) => {
  // State
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [fields, setFields] = useState<ExportField[]>(DEFAULT_FIELDS);
  const [exportFiltered, setExportFiltered] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Check if any filters are applied
  const hasFilters = selectedTags.length > 0 || selectedGroupIds.length > 0 || optedInFilter !== undefined;

  // Toggle field selection
  const toggleField = (key: string) => {
    setFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, checked: !f.checked } : f))
    );
  };

  // Select/deselect all fields
  const toggleAllFields = (checked: boolean) => {
    setFields((prev) => prev.map((f) => ({ ...f, checked })));
  };

  // Count selected fields
  const selectedFieldCount = fields.filter((f) => f.checked).length;

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    setExportSuccess(false);

    try {
      const query: QueryContactsParams = {};
      
      if (exportFiltered && hasFilters) {
        if (selectedTags.length > 0) query.tags = selectedTags;
        if (selectedGroupIds.length > 0) query.groupIds = selectedGroupIds;
        if (optedInFilter !== undefined) query.optedIn = optedInFilter;
      }

      const data = await contactsService.export(query);

      // Get selected field keys
      const selectedFields = fields.filter((f) => f.checked).map((f) => f.key);

      if (format === 'csv') {
        exportAsCSV(data, selectedFields);
      } else {
        exportAsJSON(data, selectedFields);
      }

      setExportSuccess(true);
      setTimeout(() => {
        setExportSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to export contacts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Export as CSV
  const exportAsCSV = (data: unknown[], selectedFields: string[]) => {
    const headers = selectedFields.map((key) => {
      const field = fields.find((f) => f.key === key);
      return field?.label || key;
    });

    const rows = (data as Record<string, unknown>[]).map((contact) => {
      return selectedFields.map((key) => {
        const value = contact[key];
        
        if (key === 'tags' && Array.isArray(value)) {
          return value.join('; ');
        }
        if (key === 'optedIn') {
          return value ? 'Yes' : 'No';
        }
        if (key === 'country' && typeof value === 'string') {
          const country = getCountryByCode(value);
          return country ? country.name : value;
        }
        if ((key === 'createdAt' || key === 'updatedAt') && value) {
          return new Date(value as string).toISOString();
        }
        if (key === 'customFields' && typeof value === 'object') {
          return JSON.stringify(value);
        }
        
        return value || '';
      });
    });

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    downloadFile(csv, 'text/csv', 'csv');
  };

  // Export as JSON
  const exportAsJSON = (data: unknown[], selectedFields: string[]) => {
    const filteredData = (data as Record<string, unknown>[]).map((contact) => {
      const filtered: Record<string, unknown> = {};
      selectedFields.forEach((key) => {
        if (key === 'country' && typeof contact[key] === 'string') {
          const country = getCountryByCode(contact[key] as string);
          filtered[key] = country ? { code: contact[key], name: country.name } : contact[key];
        } else {
          filtered[key] = contact[key];
        }
      });
      return filtered;
    });

    const json = JSON.stringify(filteredData, null, 2);
    downloadFile(json, 'application/json', 'json');
  };

  // Download file helper
  const downloadFile = (content: string, mimeType: string, extension: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts-${new Date().toISOString().split('T')[0]}.${extension}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle close and reset
  const handleClose = () => {
    setFormat('csv');
    setFields(DEFAULT_FIELDS);
    setExportFiltered(true);
    setExportSuccess(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Export Contacts"
      subtitle="Download your contacts in your preferred format"
      showCloseButton
    >
      <div className="p-6 space-y-6">
        {/* Success State */}
        {exportSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-8"
          >
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-white font-medium">Export Complete!</p>
            <p className="text-sm text-gray-400">Your download should start automatically</p>
          </motion.div>
        ) : (
          <>
            {/* Export Scope */}
            {hasFilters && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-white">Export Scope</h4>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setExportFiltered(false)}
                    className={`flex-1 p-4 rounded-lg border transition-colors ${
                      !exportFiltered
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <Users className={`w-5 h-5 mx-auto mb-2 ${!exportFiltered ? 'text-orange-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${!exportFiltered ? 'text-orange-400' : 'text-white'}`}>
                      All Contacts
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{totalContacts} contacts</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportFiltered(true)}
                    className={`flex-1 p-4 rounded-lg border transition-colors ${
                      exportFiltered
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <Filter className={`w-5 h-5 mx-auto mb-2 ${exportFiltered ? 'text-orange-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${exportFiltered ? 'text-orange-400' : 'text-white'}`}>
                      Filtered Only
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Current view</p>
                  </button>
                </div>

                {exportFiltered && (
                  <div className="flex flex-wrap gap-2 text-xs">
                    {selectedTags.length > 0 && (
                      <Badge variant="default">Tags: {selectedTags.join(', ')}</Badge>
                    )}
                    {selectedGroupIds.length > 0 && (
                      <Badge variant="default">
                        Groups: {selectedGroupIds.map((id) => groups.find((g) => g.id === id)?.name || id).join(', ')}
                      </Badge>
                    )}
                    {optedInFilter !== undefined && (
                      <Badge variant={optedInFilter ? 'success' : 'warning'}>
                        {optedInFilter ? 'Opted In' : 'Opted Out'}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Format Selection */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-white">Export Format</h4>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormat('csv')}
                  className={`flex-1 p-4 rounded-lg border transition-colors ${
                    format === 'csv'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <FileSpreadsheet className={`w-6 h-6 mx-auto mb-2 ${format === 'csv' ? 'text-orange-400' : 'text-gray-400'}`} />
                  <p className={`text-sm font-medium ${format === 'csv' ? 'text-orange-400' : 'text-white'}`}>CSV</p>
                  <p className="text-xs text-gray-500 mt-1">Spreadsheet compatible</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormat('json')}
                  className={`flex-1 p-4 rounded-lg border transition-colors ${
                    format === 'json'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <FileJson className={`w-6 h-6 mx-auto mb-2 ${format === 'json' ? 'text-orange-400' : 'text-gray-400'}`} />
                  <p className={`text-sm font-medium ${format === 'json' ? 'text-orange-400' : 'text-white'}`}>JSON</p>
                  <p className="text-xs text-gray-500 mt-1">Developer friendly</p>
                </button>
              </div>
            </div>

            {/* Field Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-white">Fields to Export</h4>
                <div className="flex items-center gap-2">
                  <Badge variant="default">{selectedFieldCount} selected</Badge>
                  <button
                    type="button"
                    onClick={() => toggleAllFields(selectedFieldCount < fields.length)}
                    className="text-xs text-orange-400 hover:text-orange-300"
                  >
                    {selectedFieldCount === fields.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>
              
              <Card className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  {fields.map((field) => (
                    <label
                      key={field.key}
                      className="flex items-center gap-2 cursor-pointer hover:text-white transition-colors"
                    >
                      <Checkbox
                        checked={field.checked}
                        onChange={() => toggleField(field.key)}
                      />
                      <span className={`text-sm ${field.checked ? 'text-white' : 'text-gray-400'}`}>
                        {field.label}
                      </span>
                    </label>
                  ))}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {!exportSuccess && (
        <div className="px-6 py-4 border-t border-(--color-border-gray) flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || selectedFieldCount === 0}
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export {format.toUpperCase()}
              </>
            )}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default ExportContactsModal;

